import { quickPayPath } from '@components/modules/payment/urls';
import { Icon } from '@components/ui/atoms/Icons';
import { MENU_PERMISSIONS, usePermissionCheck } from '@shared/permissions';
import {
  CardCoin,
  CardPos,
  //Chart,
  DirectboxSend,
  ElementPlus,
  Link2,
  MessageQuestion,
  //Mobile,
  MoneySend,
  RepeatCircle,
  ScanBarcode,
  Send2,
  ShoppingCart,
} from 'iconsax-reactjs';

import type { SidebarMenuItem } from '../types';

export const useMenuStructure = () => {
  const { hasAny } = usePermissionCheck();

  const menuItems: SidebarMenuItem[] = [
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, ElementPlus),
      url: '/dashboard',
      label: 'Dashboard',
      id: 'dashboard',
      type: 'itemGroup' as const,
      children: [],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, CardCoin),
      label: 'Transactions',
      id: 'transactions',
      type: 'itemGroup' as const,
      children: [
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, CardPos),
          url: '/payments',
          label: 'Payments',
          id: 'payments',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Link2),
          url: '/pay-links',
          label: 'Pay Links',
          id: 'payLinks',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, RepeatCircle),
          url: '/recurring-payment',
          label: 'Recurring Payments',
          id: 'subscriptions',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, ShoppingCart),
          url: '/checkouts',
          label: 'Checkouts',
          id: 'checkouts',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, Send2),
          url: '/payouts',
          label: 'Payouts',
          id: 'payouts',
          type: 'item' as const,
        },
        {
          icon: (type: 'active' | 'inactive') => renderIcon(type, ScanBarcode),
          url: quickPayPath,
          label: 'Quick Pay',
          id: 'quickPay',
          type: 'item' as const,
        },
        // {
        //   icon: (type: 'active' | 'inactive') => renderIcon(type, Mobile),
        //   url: '/mobile-app',
        //   label: 'Mobile App',
        //   id: 'mobile-app',
        //   type: 'item' as const,
        // },
      ],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, MoneySend),
      url: '/refunds',
      label: 'Refunds',
      id: 'refunds',
      type: 'itemGroup' as const,
      children: [],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, MessageQuestion),
      url: '/disputes',
      label: 'Disputes',
      id: 'disputes',
      type: 'itemGroup' as const,
      children: [],
    },
    {
      icon: (type: 'active' | 'inactive') => renderIcon(type, DirectboxSend),
      url: '/transfers',
      label: 'Transfers',
      id: 'transfers',
      type: 'itemGroup' as const,
      children: [],
    },
    // {
    //   icon: (type: 'active' | 'inactive') => renderIcon(type, Chart),
    //   url: '/reports',
    //   label: 'Reports',
    //   id: 'reports',
    //   type: 'itemGroup' as const,
    //   children: [],
    // },
  ];

  return menuItems
    .map((menuItem) => {
      // Filter children first based on their permissions
      if (menuItem.children && menuItem.children.length > 0) {
        const filteredChildren = menuItem.children.filter((child) => {
          const required = MENU_PERMISSIONS[child.id];

          return !required || hasAny(required);
        });

        return { ...menuItem, children: filteredChildren };
      }

      return menuItem;
    })
    .filter((menuItem) => {
      // For parent menus with children: show only if at least one child is visible
      if (menuItem.children && menuItem.children.length > 0) {
        return menuItem.children.length > 0;
      }
      // For menus without children: check their own permission

      const required = MENU_PERMISSIONS[menuItem.id];

      return !required || hasAny(required);
    });
};

function renderIcon(
  type: 'active' | 'inactive',
  IconComponent: React.ComponentType<{
    size?: string | number;
    variant?: 'Bold' | 'Linear' | 'Outline' | 'Broken' | 'Bulk' | 'TwoTone';
    color?: string;
  }>
) {
  const variant = type === 'active' ? 'Bold' : 'Linear';

  return (
    <Icon color="body.dark" size="sm">
      <IconComponent size="20" variant={variant} />
    </Icon>
  );
}
